import { Genders, Nationalities, SchoolUserRole, UserContexts } from '@schooly/constants';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CancelToken } from 'axios';
import { format, isAfter, isBefore } from 'date-fns';
import { useState } from 'react';

import { DEFAULT_DATE_FORMAT_FNS } from '../constants';
import {
  ChangeGroupSubjectAttendanceDefault,
  CheckGroupUniquenessRequest,
  CheckGroupUniquenessResponse,
  CreateGroupResponse,
  GetGroupDetailsRequest,
  GetGroupDetailsResponse,
  GetGroupsAggregatedDataRequest,
  GetGroupSubjectsProps,
  GetGroupSubjectsResponse,
  GetMembersForGroupRequest,
  GroupsAggregatedData,
  GroupsExportListRequest,
  GroupsExportListResponse,
} from './apiTypes/endpoints/groups';
import {
  AvailableRelationGroupRequest,
  Group,
  GroupLimitationsUpdate,
  GroupType,
  GroupUpdate,
  RelationGroup,
  UpdateGroupForRelationRequest,
} from './apiTypes/groups';
import {
  ApiError,
  AvailableCriteria,
  FilterElementType,
  GroupResponseType,
  IListGroupArguments,
  SORT_DIRECTION,
} from './apiTypes/misc';
import {
  RQUseInfiniteQueryOptions,
  RQUseMutationOptions,
  RQUseMutationResult,
  RQUseQueryOptions,
} from './apiTypes/query';
import {
  FilterKeys,
  UserWithGroupMembership,
  UserWithGroupMembershipNormalized,
} from './apiTypes/users';
import {
  buildExportStudentsDetailPath,
  buildGroupsAggregateListPath,
  buildGroupsAvailableCriteriaListPath,
  buildGroupsAvailableForRelationDetailPath,
  buildGroupsCreatePath,
  buildGroupsDeletePath,
  buildGroupsDetailPath,
  buildGroupsForRelationDetailPath,
  buildGroupsForRelationPartialUpdatePath,
  buildGroupsForUserDetailPath,
  buildGroupsListPath,
  buildGroupsMembersForGroupDetailPath,
  buildGroupsPartialUpdatePath,
  buildGroupsSubjectsAttendancePartialUpdatePath,
  buildGroupsSubjectsListPath,
} from './generated/schooly-api-v1/api-paths';
import * as api from './requests';
import { getSortParams } from './utils/getSortParam';
import { newDateTimezoneOffset } from './utils/newDateTimezoneOffset';
import { removeObjectUndefinedNullValues } from './utils/removeObjectUndefinedNullValues';
import { buildCheckGroupUniquenessPath, buildGroupsExportPath } from './apiTypes/paths/groups';

export const MAX_PAGE_SIZE = 9999;
const DEFAULT_PAGE_SIZE = 50;

const GROUPS_URL = '/groups/';
const GROUPS_AGGREGATED_DATA_URL = '/groups/aggregate';

export const GET_GROUP_DETAILS_QUERY = `${GROUPS_URL}/GET_GROUP_DETAILS_QUERY`;
export const GET_MEMBERS_FOR_GROUP_QUERY = `${GROUPS_URL}/GET_MEMBERS_FOR_GROUP_QUERY`;

export function listGroups<MinFlag extends boolean | undefined = undefined>({
  schoolId,
  pageSize = DEFAULT_PAGE_SIZE,
  pageNumber,
  query,
  filters,
  token,
  sort,
  relationIds,
  singleDate,
  min,
}: Omit<IListGroupArguments, 'min'> & { min?: MinFlag }): Promise<GroupResponseType<MinFlag>> {
  const onlyTutorGroups = filters?.only_tutor_groups?.join('') === '1';

  const params = removeObjectUndefinedNullValues({
    page_size: pageSize,
    page_number: pageNumber,

    // TODO: uncomment when backend is ready to process student/staff props sort
    // sort_by: getSortParam(
    //   x ? { columnTextId: arrangeBy, direction: SORT_DIRECTION.ASC } : sort,
    // ),
    sort_by: getSortParams(sort),

    query: query?.toLowerCase(),
    school_id: schoolId,

    date_from: filters?.date?.[0],
    date_to: filters?.date?.[1] || filters?.date?.[0],

    intersect_date_from: filters?.intersect_date?.[0],
    intersect_date_to: filters?.intersect_date?.[1] || filters?.intersect_date?.[0],

    single_date: filters?.single_date?.[0] || singleDate,

    student_status: filters?.student_status?.join(','),
    staff_status: filters?.staff_status?.join(','),

    age_group: filters?.age_group?.join(',') || filters?.student_age_group?.join(','),
    //staff_age_group: filters?.staff_age_group?.join(','),

    student_house: filters?.student_house?.join(','),
    staff_house: filters?.staff_house?.join(','),

    subject: onlyTutorGroups ? undefined : filters?.subject?.join(','),
    group_type: onlyTutorGroups ? GroupType.TutorGroup : filters?.group_types?.join(','),

    assessment_ids: filters?.assessment_ids?.join(','),

    relation_ids:
      relationIds?.length || filters?.staff?.length
        ? [...(relationIds || []), ...(filters?.staff || [])].join(',')
        : undefined,
    min: min ? 1 : undefined,
  });

  return api.get(buildGroupsListPath(), { params, cancelToken: token });
}

export const GET_GROUPS_QUERY = `${GROUPS_URL}/GET_GROUPS_QUERY`;

export const GROUPS_QUERY_FILTER_KEYS = [
  FilterKeys.Date,
  FilterKeys.IntersectDate,
  FilterKeys.SingleDate,
  FilterKeys.AgeGroup,
  FilterKeys.StaffHouse,
  FilterKeys.StudentHouse,
  FilterKeys.StaffStatus,
  FilterKeys.StudentStatus,
  FilterKeys.Subject,
  FilterKeys.OnlyTutorGroups,
  FilterKeys.Staff,
  FilterKeys.Assessment,
] as const;

export type GroupArrangeBy = FilterKeys.Subject | FilterKeys.AgeGroup;

export type GetGroupsQueryFilters = {
  [FilterKeys.Date]?: string[];
  [FilterKeys.IntersectDate]?: string[];
  [FilterKeys.SingleDate]?: string[];
  [FilterKeys.AgeGroup]?: string[];
  [FilterKeys.StaffHouse]?: string[];
  [FilterKeys.StudentHouse]?: string[];
  [FilterKeys.StaffStatus]?: string[];
  [FilterKeys.StudentStatus]?: string[];
  [FilterKeys.Staff]?: string[];
  [FilterKeys.OnlyTutorGroups]?: string[];
  [FilterKeys.Subject]?: string[];
  [FilterKeys.Assessment]?: string[];
  [FilterKeys.GroupTypes]?: GroupType[];
};

export const GROUPS_ARRANGE_BY_FILTER_KEYS = [FilterKeys.Subject, FilterKeys.AgeGroup] as const;

export type GetGroupsQuerySort = {
  columnTextId: 'name' | 'students' | 'staff' | 'subject' | 'validity';
  direction: SORT_DIRECTION;
};

export const useGetGroupsQuery = <MinFlag extends boolean | undefined = undefined>(
  initialParams: Omit<IListGroupArguments, 'filters' | 'sort' | 'min'> & {
    filters: GetGroupsQueryFilters;
    sort?: GetGroupsQuerySort;
    min?: MinFlag;
  },
  options?: RQUseInfiniteQueryOptions<GroupResponseType<MinFlag>>,
) => {
  const queryClient = useQueryClient();
  const [params, setParams] = useState(initialParams);

  const query = useInfiniteQuery<GroupResponseType<MinFlag>, ApiError>(
    [GET_GROUPS_QUERY, params],
    ({ pageParam }) => listGroups({ pageNumber: pageParam, ...params }),
    {
      getNextPageParam: (lastPage) => {
        return !lastPage?.total_pages || lastPage?.current_page === lastPage?.total_pages
          ? undefined
          : lastPage.next_page;
      },
      getPreviousPageParam: (firstPage) => {
        return firstPage?.current_page ? firstPage?.previous_page : undefined;
      },
      onSuccess: (data) => {
        for (const page of data.pages) {
          for (const group of page.results) {
            queryClient.setQueryData([GET_GROUP_DETAILS_QUERY, group.id], { group });
          }
        }
      },
      ...options,
    },
  );

  return { ...query, setParams, params };
};

export function getGroupDetails({
  id,
  date,
  date_from,
  date_to,
  with_absence_request,
}: GetGroupDetailsRequest): Promise<GetGroupDetailsResponse> {
  const params = removeObjectUndefinedNullValues({
    date,
    date_from,
    date_to,
    with_absence_request,
  });
  return api.get(buildGroupsDetailPath({ id }), { params });
}

export const useGetGroupDetailsQuery = (
  params: GetGroupDetailsRequest,
  options?: RQUseQueryOptions<GetGroupDetailsResponse>,
) => {
  return useQuery<GetGroupDetailsResponse, ApiError>(
    [
      GET_GROUP_DETAILS_QUERY,
      params.id,
      JSON.stringify({
        date: params.date,
        date_from: params.date_from,
        date_to: params.date_to,
        with_absence_request: params.with_absence_request,
      }),
    ],
    () => getGroupDetails(params),
    options,
  );
};

export function getGroupMembersForGroup({
  id,
  date,
  date_from,
  date_to,
  user_role,
}: GetMembersForGroupRequest): Promise<{ users: UserWithGroupMembership[] }> {
  return api.get(buildGroupsMembersForGroupDetailPath({ id }), {
    params: removeObjectUndefinedNullValues({
      date,
      date_from,
      date_to,
      user_role,
    }),
  });
}

export const useGetMembersForGroupQuery = (
  params: GetMembersForGroupRequest,
  options?: RQUseQueryOptions<{ users: UserWithGroupMembership[] }>,
) => {
  return useQuery<{ users: UserWithGroupMembership[] }, ApiError>(
    [
      GET_MEMBERS_FOR_GROUP_QUERY,
      params.id,
      params.user_role,
      { date: params.date, date_from: params.date_from, date_to: params.date_to },
    ],
    () => getGroupMembersForGroup(params),
    options,
  );
};

/**
 * Does the `useGetMembersForGroupQuery` and groups members by actual/not actual
 * (transforms the original response).
 */
export const useGetActualMembersForGroupQuery = (
  params: GetMembersForGroupRequest,
  options?: RQUseQueryOptions<{
    actual: UserWithGroupMembershipNormalized[];
    notActual: UserWithGroupMembershipNormalized[];
  }>,
) => {
  return useQuery<
    {
      actual: UserWithGroupMembershipNormalized[];
      notActual: UserWithGroupMembershipNormalized[];
    },
    ApiError
  >(
    [
      GET_MEMBERS_FOR_GROUP_QUERY,
      params.id,
      params.user_role,
      { data: params.date, date_from: params.date_from, date_to: params.date_to },
    ],
    () => getGroupMembersForGroup(params) as any,
    {
      // Using cast to `any` as the data is being mutated and the eventual result will be
      // totally different from the response.
      select: ((response: { users: UserWithGroupMembership[] }) => {
        const currentDate = newDateTimezoneOffset(params.date);
        const actualUsers: Record<string, UserWithGroupMembershipNormalized> = {};
        const notActualUsers: Record<string, UserWithGroupMembershipNormalized> = {};

        response.users.forEach((u) => {
          const {
            start,
            end,
            school_properties,
            individual,
            criteria_type,
            enum_criteria,
            ...userData
          } = u;

          const memberStartDateTime = newDateTimezoneOffset(start);
          const memberEndDateTime = newDateTimezoneOffset(end);

          const container =
            isBefore(memberEndDateTime, currentDate) || isAfter(memberStartDateTime, currentDate)
              ? notActualUsers
              : actualUsers;
          const duplicate = container[u.relation_id];

          if (duplicate) {
            container[u.relation_id] = {
              ...userData,
              membership: [
                ...duplicate.membership,
                { start, end, criteria_type, enum_criteria, school_properties, individual },
              ],
            };
          } else {
            container[u.relation_id] = {
              ...userData,
              membership: [
                { start, end, criteria_type, enum_criteria, school_properties, individual },
              ],
            };
          }
        });

        const actual = Object.values(actualUsers);
        const notActual = Object.values(notActualUsers).filter(
          ({ relation_id }) => !actual.some((u) => u.relation_id === relation_id),
        );

        if (!actual.length && !Object.values(notActualUsers).length) {
          return { actual: [], notActual: [] };
        }

        return {
          actual,
          notActual,
        };
      }) as any,
      ...options,
    },
  );
};

export function createGroup(schoolId: string, group: GroupUpdate): Promise<CreateGroupResponse> {
  const data = {
    group: {
      school_id: schoolId,
      ...group,
    },
  };

  if ((data.group.limited_to_students as GroupLimitationsUpdate)?.subject_ids) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { subject_ids, ...limitedToStudents } = data.group
      .limited_to_students as GroupLimitationsUpdate;
    data.group.limited_to_students = limitedToStudents;
  }

  if ((data.group.limited_to_students as GroupLimitationsUpdate)?.subject_ids) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { subject_ids, ...limitedToStudents } = data.group
      .limited_to_students as GroupLimitationsUpdate;
    data.group.limited_to_students = limitedToStudents;
  }

  return api.post(buildGroupsCreatePath(), data);
}

export function updateGroup(groupId: string, groupUpdate: GroupUpdate): Promise<{ group: Group }> {
  const data = {
    group: groupUpdate,
  };

  return api.patch(buildGroupsPartialUpdatePath({ id: groupId }), data);
}

export function getGroupSubjects({
  schoolId,
  showArchived = true,
}: GetGroupSubjectsProps): Promise<GetGroupSubjectsResponse> {
  const params = removeObjectUndefinedNullValues({
    school_id: schoolId,
    show_archived: showArchived,
  });
  return api.get(buildGroupsSubjectsListPath(), { params });
}

export const GET_GROUP_SUBJECTS_QUERY = `${GROUPS_URL}GET_GROUP_SUBJECTS_QUERY`;

export const useGetGroupSubjectsQuery = (
  params: GetGroupSubjectsProps,
  options?: RQUseQueryOptions<GetGroupSubjectsResponse>,
) => {
  return useQuery<GetGroupSubjectsResponse, ApiError>(
    [GET_GROUP_SUBJECTS_QUERY, params.schoolId, params.showArchived],
    () => getGroupSubjects(params),
    {
      ...options,
    },
  );
};

export function changeGroupSubjectAttendanceDefault({
  schoolId,
  subjects,
}: ChangeGroupSubjectAttendanceDefault): Promise<{ success: string }> {
  return api.patch(buildGroupsSubjectsAttendancePartialUpdatePath({ school_id: schoolId }), {
    subjects,
  });
}

interface GroupAvailableCriteriaParams {
  schoolId: string;
  fromDate: string;
  toDate?: string;
  relationRole?: SchoolUserRole;
  schoolPropertyIds?: string[];
  genders?: Genders[];
  nationalities?: Nationalities[];
  token?: CancelToken | undefined;
  type?: FilterElementType;
  showNoneCounts?: boolean;
  searchQuery?: string;
}

const prepareGroupAvailableCriteriaRequestParams = ({
  schoolId,
  fromDate,
  toDate,
  relationRole,
  schoolPropertyIds,
  genders,
  nationalities,
  showNoneCounts,
  type,
  searchQuery,
}: GroupAvailableCriteriaParams) => ({
  school_id: schoolId,
  relation_role: relationRole,
  date_from: fromDate || format(newDateTimezoneOffset(), DEFAULT_DATE_FORMAT_FNS),
  type,
  show_none_counts: showNoneCounts ? '1' : undefined,
  date_to: toDate,
  ...(schoolPropertyIds?.length && {
    limited_to_school_property_ids: schoolPropertyIds?.join(','),
  }),
  ...(genders?.length && {
    limited_to_genders: genders?.join(','),
  }),
  ...(nationalities?.length && {
    limited_to_nationalities: nationalities?.join(','),
  }),
  ...(searchQuery?.length && {
    search_query: searchQuery,
  }),
});

export const GET_GROUP_AVAILABLE_CRITERIA_QUERY = `${GROUPS_URL}/GET_GROUP_AVAILABLE_CRITERIA_QUERY`;

export const useGetGroupAvailableCriteria = (
  params: GroupAvailableCriteriaParams,
  options?: RQUseQueryOptions<{ available_criteria: AvailableCriteria[] }>,
) => {
  return useQuery<{ available_criteria: AvailableCriteria[] }, ApiError>(
    [GET_GROUP_AVAILABLE_CRITERIA_QUERY, params],
    () => getGroupAvailableCriteria(params),
    options,
  );
};

export function getGroupAvailableCriteria(
  params: GroupAvailableCriteriaParams,
): Promise<{ available_criteria: AvailableCriteria[] }> {
  return api.get(buildGroupsAvailableCriteriaListPath(), {
    params: prepareGroupAvailableCriteriaRequestParams(params),
    cancelToken: params.token,
  });
}

export function checkGroupUniqueness(
  requestParams: CheckGroupUniquenessRequest,
): Promise<CheckGroupUniquenessResponse> {
  return api.get(buildCheckGroupUniquenessPath(), {
    params: removeObjectUndefinedNullValues(requestParams),
  });
}

export function deleteGroup(groupId: string): Promise<{}> {
  return api.remove(buildGroupsDeletePath({ id: groupId }));
}

// TODO move all interfaces to /apiTypes/
export interface UserGroupsRequest {
  context: UserContexts;
  relationId: string;
  date_from?: string;
  date_to?: string;
}

export interface RelationGroups {
  groups: RelationGroup[];
  count: number;
}

export type GroupForRelationRequest = Omit<UserGroupsRequest, 'context'> & {
  search_query?: string;
};

export function getGroupsForUser({
  context,
  relationId,
  date_from,
  date_to,
}: UserGroupsRequest): Promise<{ groups: Group[] }> {
  const params = {
    date_from,
    date_to,
  };

  return api.get(buildGroupsForUserDetailPath({ id: relationId }), {
    params: { ...params, context },
  });
}

export function getGroupsForRelation({
  relationId,
  ...params
}: GroupForRelationRequest): Promise<RelationGroups> {
  return api.get(buildGroupsForRelationDetailPath({ relation_id: relationId }), { params });
}

export const GET_GROUPS_FOR_RELATION_QUERY = `${GROUPS_URL}GET_GROUPS_FOR_RELATION_QUERY`;

export const useGetGroupsForRelationQuery = (
  params: GroupForRelationRequest,
  options?: RQUseQueryOptions<RelationGroups>,
) => {
  return useQuery<RelationGroups, ApiError>(
    [GET_GROUPS_FOR_RELATION_QUERY, params.relationId, params],
    () => getGroupsForRelation(params),
    options,
  );
};

export const GET_AVAILABLE_RELATION_GROUPS_QUERY = `${GROUPS_URL}GET_AVAILABLE_RELATION_GROUPS_QUERY`;

const getAvailableRelationGroups = ({
  relationId,
  filters,
  search_query,
}: AvailableRelationGroupRequest): Promise<{ groups: Group[]; count: number }> => {
  const isTutorGroups = filters?.only_tutor_groups?.[0] === '1';

  const params = removeObjectUndefinedNullValues({
    subject_ids: !isTutorGroups ? filters?.subject?.join(',') : undefined,
    group_type: isTutorGroups ? GroupType.TutorGroup : undefined,
    date_from: filters?.date?.[0],
    date_to: filters?.date?.[1] || filters?.date?.[0],
    search_query,
  });

  return api.get(buildGroupsAvailableForRelationDetailPath({ relation_id: relationId }), {
    params,
  });
};

export const useGetAvailableRelationGroupsQuery = (
  params: AvailableRelationGroupRequest,
  options?: RQUseQueryOptions<{ groups: Group[]; count: number }>,
) => {
  return useQuery<{ groups: Group[]; count: number }, ApiError>(
    [GET_AVAILABLE_RELATION_GROUPS_QUERY, params.relationId, params],
    () => getAvailableRelationGroups(params),
    {
      ...options,
    },
  );
};

export function getGroupsAggregatedData({
  schoolId,
  filters,
  arrangeBy,
  query,
}: GetGroupsAggregatedDataRequest): Promise<GroupsAggregatedData> {
  const onlyTutorGroups = filters?.only_tutor_groups?.join('') === '1';

  const params = removeObjectUndefinedNullValues({
    school_id: schoolId,

    date_from: filters?.date?.[0],
    date_to: filters?.date?.[1] || filters?.date?.[0],

    student_status: filters?.student_status?.join(','),
    staff_status: filters?.staff_status?.join(','),

    age_group: filters?.age_group?.join(','),

    student_house: filters?.student_house?.join(','),
    staff_house: filters?.staff_house?.join(','),

    subject: onlyTutorGroups ? undefined : filters?.subject?.join(','),
    group_type: onlyTutorGroups ? GroupType.TutorGroup : undefined,

    relation_ids: filters?.staff?.join(','),
    arrange_by: arrangeBy,
    query: query,
  });
  return api.get(buildGroupsAggregateListPath(), { params });
}

export function updateGroupsForRelation({
  relationId,
  ...body
}: UpdateGroupForRelationRequest): Promise<Group[]> {
  return api.patch(buildGroupsForRelationPartialUpdatePath({ relation_id: relationId }), body);
}

export const useUpdateGroupsForRelationMutation = (
  options?: RQUseMutationOptions<Group[], UpdateGroupForRelationRequest>,
): RQUseMutationResult<Group[], UpdateGroupForRelationRequest> => {
  return useMutation(updateGroupsForRelation, options);
};

export const GET_GROUPS_AGGREGATED_DATA_QUERY = `${GROUPS_AGGREGATED_DATA_URL}/GET_GROUPS_AGGREGATED_DATA_QUERY`;

export const useGetGroupsAggregatedData = (
  params: GetGroupsAggregatedDataRequest,
  options?: RQUseQueryOptions<GroupsAggregatedData>,
) => {
  return useQuery<GroupsAggregatedData, ApiError>(
    [GET_GROUPS_AGGREGATED_DATA_QUERY, params],
    () => getGroupsAggregatedData(params),
    options,
  );
};

export const GET_GROUPS_EXPORT_LIST_QUERY = `${GROUPS_URL}GET_GROUPS_EXPORT_LIST_QUERY`;

export function getGroupsExportList({
  sort_by,
  group_types,
  ...params
}: GroupsExportListRequest): Promise<GroupsExportListResponse> {
  const queryParams = removeObjectUndefinedNullValues({
    sort_by: getSortParams(sort_by),
    group_types: group_types?.join(','),
    ...params,
  });

  return api.get(buildGroupsExportPath(), { params: queryParams });
}

export const useGetGroupsExportListQuery = (
  params: GroupsExportListRequest,
  options?: RQUseQueryOptions<GroupsExportListResponse>,
) => {
  return useQuery<GroupsExportListResponse, ApiError>(
    [GET_GROUPS_EXPORT_LIST_QUERY, params],
    () => getGroupsExportList(params),
    options,
  );
};

export interface ExportGroupStudentListRequest {
  exportType: number;
  mimeType: 'application/pdf' | 'text/csv';
  groupId: string;
  studentDetails: number;
  primaryContactDetails: number;
  medicalInfo: number;
}

export function exportGroupStudentList({
  groupId,
  exportType,
  mimeType,
  studentDetails,
  primaryContactDetails,
  medicalInfo,
}: ExportGroupStudentListRequest): Promise<ArrayBuffer> {
  const params = {
    export_type: exportType,
    student_details: studentDetails,
    primary_contact_details: primaryContactDetails,
    medical_info: medicalInfo,
  };
  return api.get(buildExportStudentsDetailPath({ group_id: groupId }), {
    params,
    responseType: 'arraybuffer',
    headers: {
      'Content-Type': 'application/json',
      Accept: mimeType,
    },
  });
}
